import type { ModelLayerSpecification, Map, ExpressionSpecification } from "@iventis/mapbox-gl";
import type { Feature, Point, Geometry } from "geojson";
import type { MapObjectProperties } from "@iventis/map-types";
import { StyleValueExtractionMethod } from "@iventis/domain-model/model/styleValueExtractionMethod";
import { createMappedStyleValue, getStaticStyleValue } from "@iventis/layer-style-helpers";
import type { BehaviorSubject } from "rxjs";
import type { ModelStyle } from "@iventis/domain-model/model/modelStyle";
import { EMPTY_GUID } from "@iventis/utilities/src/constants";
import { StyleValue } from "@iventis/domain-model/model/styleValue";
import { LineModelStyle } from "@iventis/domain-model/model/lineModelStyle";
import type { ModelDataStore, ModelData } from "../../data-store/model-data-store";
import type { DataFieldListItemStore } from "../../data-store/data-field-list-item-store";
import { MapModuleLayer, MapState } from "../../types/store-schema";
import { getCustomModelLayerStyle, getModelLayerStyle } from "../../utilities/layer.helpers";
import { styleValueToMapboxStyleValue } from "../mapbox/engine-mapbox-helpers";
import type { ModelLayerStyle } from "../../types/models";
import type { StylePropertyToValueMapV2 } from "../../types/style-types";
import { MapboxModelBaseLayer } from "./mapbox-model-base-layer";

export class MapboxModelCustomisableLayer<
    TSource extends Feature<Geometry, MapObjectProperties>[] = Feature<Point, MapObjectProperties>[],
    TStyle extends ModelLayerStyle = ModelStyle
> extends MapboxModelBaseLayer<TSource, TStyle> {
    protected readonly dataFieldListItemStore: DataFieldListItemStore;

    constructor(
        map: Map,
        layer: MapModuleLayer,
        state$: BehaviorSubject<MapState>,
        modelDataStore: ModelDataStore,
        dataFieldListItemDataStore: DataFieldListItemStore,
        events: {
            getAboveLayerId: (layerId: string) => string;
        }
    ) {
        super(map, layer, state$, modelDataStore, { name: layer.name, type: "base" }, events);
        this.dataFieldListItemStore = dataFieldListItemDataStore;
    }

    protected async loadModel(iventisLayer: MapModuleLayer) {
        await super.loadModel(iventisLayer);
        await this.loadDataFieldListItems(iventisLayer);
    }

    /** Loads all the datafield list items needed for a model layer style */
    protected async loadDataFieldListItems(iventisLayer: MapModuleLayer) {
        const style = getCustomModelLayerStyle(iventisLayer);
        if (this.isLayerDimensionsListItemPropertyBased(style)) {
            const { dataFieldId } = style.height;
            const listItems = await this.dataFieldListItemStore.getDataFieldListItems(dataFieldId);
            this.dataFieldListItems[dataFieldId] = listItems;
        }
    }

    public async updateStyle(change: StylePropertyToValueMapV2<TStyle>) {
        const layer = this.getIventisLayer();

        await this.loadModel(layer);
        await this.loadDataFieldListItems(layer);

        if (change.model || change.customImage || change.customColour || change.colour) {
            this.mapboxLayerManager.setLayoutProperty("model-id", this.getMapboxModelIdValue(layer));
        }

        if (change.height || change.width || change.length || change.scale) {
            this.mapboxLayerManager.setPaintProperty("model-scale", this.getMapboxModelScaleValue(layer, this.modelData));
        }
    }

    public iventisLayerToMapboxLayer(iventisLayer: MapModuleLayer, modelData: ModelData[]): ModelLayerSpecification {
        const mapboxLayer = super.iventisLayerToMapboxLayer(iventisLayer, modelData) as ModelLayerSpecification;
        mapboxLayer.paint["model-rotation"] = [
            0,
            0,
            // Gets the rotation out of a map object property, we have to inverse the rotation (Mapbox and DeckGl interpret rotation in different directions) and we add the deckgl rotation modifier
            ["+", ["*", ["get", "z", ["get", "rotation"]], -1], this.deckGlToMapboxRotationModifier],
        ];
        return mapboxLayer;
    }

    protected getMapboxModelScaleValue(iventisLayer: MapModuleLayer, modelData: ModelData[]): ModelLayerSpecification["paint"]["model-scale"] {
        const modelStyle = getCustomModelLayerStyle(iventisLayer);
        if (modelStyle == null) {
            return this.defaultScaleValue;
        }
        const useDimensions = modelData.some(this.isModelValidForDimensionalScaling);

        // Uses length, width and height style values to determine the scale of the layer
        if (useDimensions) {
            const { model, height } = modelStyle;
            const isModelDataFieldBased = model.extractionMethod === StyleValueExtractionMethod.Mapped;
            const isDimensionsDataFieldBased = this.isLayerDimensionsBasedOnDataFields(modelStyle);
            const isDimensionsUsingMappedProperty = this.isLayerDimensionsListItemPropertyBased(modelStyle);

            // Dimensions are datafield based but all the values are the same
            if (isDimensionsDataFieldBased && Object.values(height.mappedValues).length === 0) {
                return this.createDimensionalScalingValue(modelStyle);
            }

            if (isModelDataFieldBased || isDimensionsDataFieldBased || isDimensionsUsingMappedProperty) {
                // If model is datafield based, get all list item and datafield ids from the model style value.
                // Otherwise only the dimensions are datafield based so get list item and datafield ids from the height style value. Height, width and length will all use the same datafield and list item ids.
                const listItemIds = this.getListItemsForDimensionalScaling(modelStyle);

                const dataFieldId = isModelDataFieldBased ? model.dataFieldId : height.dataFieldId;

                const expression: ExpressionSpecification = ["case"];
                // For each list item being used, create a scale value
                for (const listItemId of listItemIds) {
                    const scaleValue = this.createDimensionalScalingValue(modelStyle, listItemId);
                    expression.push(["==", ["get", dataFieldId], listItemId], scaleValue);
                }

                // Add a static scale value for all map objects which don't have a list item selected
                const staticScalueValue = this.createDimensionalScalingValue(modelStyle);
                expression.push(staticScalueValue);
                return expression;
            }

            if (!isModelDataFieldBased) {
                return this.createDimensionalScalingValue(modelStyle);
            }

            throw new Error(`Combination of model ${model.extractionMethod} and dimension ${height.extractionMethod} extraction methods not handled`);
        }

        // When a model is using the scale style value to determine the scale of the layer
        const { scale } = modelStyle;

        if (scale == null) {
            return this.defaultScaleValue;
        }

        switch (scale.extractionMethod) {
            case StyleValueExtractionMethod.Static: {
                const scaleNumberValue = getStaticStyleValue(scale);
                return [scaleNumberValue, scaleNumberValue, scaleNumberValue];
            }
            default:
                throw new Error(`${scale.extractionMethod} is not supported for "Scale" style value`);
        }
    }

    protected getMapboxModelIdValue(iventisLayer: MapModuleLayer): ModelLayerSpecification["layout"]["model-id"] {
        const anyModelStyle = getModelLayerStyle(iventisLayer);
        const { model, customImage, customColour, colour } = { ...getCustomModelLayerStyle(iventisLayer), ...anyModelStyle };
        const staticModelId = model.staticValue.staticValue;
        const usingCustomColour = getStaticStyleValue(customColour);
        const customImageId = getStaticStyleValue(customImage);
        const usingCustomImage = customImageId !== EMPTY_GUID && customImage != null;
        const usingDataFieldColourAndModel =
            usingCustomColour && model.extractionMethod === StyleValueExtractionMethod.Mapped && colour.extractionMethod === StyleValueExtractionMethod.Mapped;

        if (usingCustomColour && colour.extractionMethod === StyleValueExtractionMethod.Static && model.extractionMethod === StyleValueExtractionMethod.Static) {
            const colourValue = getStaticStyleValue(colour);
            return this.createModelId(staticModelId, customImageId, colourValue);
        }

        // Datafield based colour for a single model
        if (usingCustomColour && model.extractionMethod === StyleValueExtractionMethod.Static) {
            const listItemIdToModelId: Record<string, string> = {};
            // For each colour being used, create a model id
            for (const [listItemId, colourStyleValue] of Object.entries(colour.mappedValues)) {
                listItemIdToModelId[listItemId] = this.createModelId(staticModelId, customImageId, colourStyleValue.staticValue);
            }
            // If the colour datafield has not got a value selected
            const staticModelIdWithColour = this.createModelId(staticModelId, customImageId, colour.staticValue.staticValue);
            return styleValueToMapboxStyleValue(createMappedStyleValue(staticModelIdWithColour, listItemIdToModelId, colour.dataFieldId));
        }

        // Datafield based colour for an datafield based model
        if (usingDataFieldColourAndModel && model.dataFieldId !== colour.dataFieldId) {
            return this.createModelAndColourExpressionCase(model, colour, customImageId);
        }

        // Datafield based colour and model which use the same datafield
        if (usingDataFieldColourAndModel) {
            const listItemIdToModelId: Record<string, string> = {};
            const staticColourValue = colour.staticValue.staticValue;
            // Get all unique list items used for model and colour datafield styling
            const uniqueListItemIds = [...new Set([...Object.keys(model.mappedValues), ...Object.keys(colour.mappedValues)])];
            for (const listItemId of uniqueListItemIds) {
                // Get model and colour value from mapped values, if not present use static value
                const modelValue = model.mappedValues[listItemId]?.staticValue ?? staticModelId;
                const colourValue = colour.mappedValues[listItemId]?.staticValue ?? staticColourValue;
                listItemIdToModelId[listItemId] = this.createModelId(modelValue, customImageId, colourValue);
            }
            // If the model datafield does not have a value selected
            const staticModelIdWithImage = this.createModelId(staticModelId, customImageId, staticColourValue);
            return styleValueToMapboxStyleValue(createMappedStyleValue(staticModelIdWithImage, listItemIdToModelId, model.dataFieldId));
        }

        // Using static custom colour and data driven model
        if (usingCustomColour && model.extractionMethod === StyleValueExtractionMethod.Mapped) {
            const listItemIdToModelId: Record<string, string> = {};
            const colourStaticValue = getStaticStyleValue(colour);
            // For each model list item create a model id with the image added to it
            for (const [listItemId, modelStyleValue] of Object.entries(model.mappedValues)) {
                listItemIdToModelId[listItemId] = this.createModelId(modelStyleValue.staticValue, customImageId, colourStaticValue);
            }
            // If the model datafield does not have a value selected
            const staticModelWithColour = this.createModelId(staticModelId, customImageId, colourStaticValue);
            return styleValueToMapboxStyleValue(createMappedStyleValue(staticModelWithColour, listItemIdToModelId, model.dataFieldId));
        }

        // Model has a custom image and model is atttribute based
        if (usingCustomImage && model.extractionMethod === StyleValueExtractionMethod.Mapped) {
            const listItemIdToModelId: Record<string, string> = {};
            // For each model list item create a model id with the image added to it
            for (const [listItemId, modelStyleValue] of Object.entries(model.mappedValues)) {
                listItemIdToModelId[listItemId] = this.createModelId(modelStyleValue.staticValue, customImageId);
            }
            // If the model datafield does not have a value selected
            const staticModelIdWithImage = this.createModelId(staticModelId, customImageId);
            return styleValueToMapboxStyleValue(createMappedStyleValue(staticModelIdWithImage, listItemIdToModelId, model.dataFieldId));
        }

        // Model has custom image and only a single model
        if (usingCustomImage) {
            return this.createModelId(staticModelId, customImageId);
        }

        return super.getMapboxModelIdValue(iventisLayer);
    }

    /** Checks whether a model can be used for dimension based scale value */
    private isModelValidForDimensionalScaling(model: ModelData) {
        return model.height != null && model.height !== 0 && model.width != null && model.width !== 0 && model.length != null && model.length !== 0;
    }

    /** Will create a scale value for a layer and model combination */
    private createDimensionalScalingValue(style: ModelLayerStyle, listItemId?: string) {
        const { model, height, width, length } = style;

        // Ensure the model has dimension style values, otherwise just return default scale value
        if (width == null || length == null || height == null) {
            return this.defaultScaleValue;
        }

        // Find the model which is being used
        const modelId = model.mappedValues?.[listItemId]?.staticValue ?? model.staticValue.staticValue;
        const selectedModelData = this.modelData.find((modelData) => modelData.baseModelId === modelId);
        const canModelBeScaledByDimensions = this.isModelValidForDimensionalScaling(selectedModelData);

        if (selectedModelData == null) {
            throw new Error(`Model with id "${modelId}" is not loaded.`);
        }

        // Model can't be scaled by dimension, so return scale value so model is original size
        if (!canModelBeScaledByDimensions) {
            return this.defaultScaleValue;
        }

        if (this.isLayerDimensionsListItemPropertyBased(style) && listItemId != null) {
            const listItem = this.dataFieldListItems[height.dataFieldId]?.find((listItem) => listItem.id === listItemId);

            if (listItem == null) {
                // If we can't find list item, it could be because it has been deleted. Therefore set to default scale
                return this.defaultScaleValue;
            }

            // If a property value does not exist, then use the model dimension value
            const heightPropertyValue =
                listItem.propertyValues.find((propertyValue) => propertyValue.propertyId === height.dataFieldListItemPropertyId)?.number ?? selectedModelData.height;
            const widthPropertyValue =
                listItem.propertyValues.find((propertyValue) => propertyValue.propertyId === width.dataFieldListItemPropertyId)?.number ?? selectedModelData.width;
            const lengthPropertyValue =
                listItem.propertyValues.find((propertyValue) => propertyValue.propertyId === length.dataFieldListItemPropertyId)?.number ?? selectedModelData.length;

            return [widthPropertyValue / selectedModelData.width, lengthPropertyValue / selectedModelData.length, heightPropertyValue / selectedModelData.height];
        }

        // Calculate the scale value for each width, length and height of the model
        const widthStyleValue = listItemId ? width.mappedValues[listItemId].staticValue : width.staticValue.staticValue;
        const lengthStyleValue = listItemId ? length.mappedValues[listItemId].staticValue : length.staticValue.staticValue;
        const heightStyleValue = listItemId ? height.mappedValues[listItemId].staticValue : height.staticValue.staticValue;
        return [widthStyleValue / selectedModelData.width, lengthStyleValue / selectedModelData.length, heightStyleValue / selectedModelData.height];
    }

    /**
     * Creates a set of case statments for when a model and colour are both datafield based.
     * This will include:
     * 1. Model and colour both have datafield values selected (every value combination)
     * 2. Model has datafield value selected, colour has not got a datafield value selected
     * 3. Model has not got a datafield value selected, colour does have a datafield value selected
     * 4. Model and colour datafield values are not selected
     */
    private createModelAndColourExpressionCase(modelStyleValue: StyleValue<string>, colourStyleValue: StyleValue<string>, customImageId: string) {
        const expression: ExpressionSpecification = ["case"];
        // Create a model id for each combination of model and colour
        for (const [colourListItemId, colourValue] of Object.entries(colourStyleValue.mappedValues)) {
            for (const [modelListItemId, modelValue] of Object.entries(modelStyleValue.mappedValues)) {
                // When the model and colour datafield both have values populated
                expression.push([
                    "==",
                    true,
                    ["all", ["==", ["get", colourStyleValue.dataFieldId], colourListItemId], ["==", ["get", modelStyleValue.dataFieldId], modelListItemId]],
                ]);
                expression.push(this.createModelId(modelValue.staticValue, customImageId, colourValue.staticValue));
            }
            // When model datafield value is null and colour datafield value is populated
            expression.push(["==", true, ["all", ["==", ["get", colourStyleValue.dataFieldId], colourListItemId], ["==", ["get", modelStyleValue.dataFieldId], null]]]);
            expression.push(this.createModelId(modelStyleValue.staticValue.staticValue, customImageId, colourValue.staticValue));
        }

        for (const [modelListItemId, modelValue] of Object.entries(modelStyleValue.mappedValues)) {
            // When colour datafield value is null and the model datafield value is populated
            expression.push(["==", true, ["all", ["==", ["get", colourStyleValue.dataFieldId], null], ["==", ["get", modelStyleValue.dataFieldId], modelListItemId]]]);
            expression.push(this.createModelId(modelValue.staticValue, customImageId, colourStyleValue.staticValue.staticValue));
        }

        // If neither the model or colour datafield have a value selected
        expression.push(this.createModelId(modelStyleValue.staticValue.staticValue, customImageId, colourStyleValue.staticValue.staticValue));
        return expression;
    }

    /** Depending on what the model style is changes what list item ids we use to create the scale value */
    private getListItemsForDimensionalScaling(modelStyle: LineModelStyle | ModelStyle) {
        const { height } = modelStyle;
        if (this.isLayerDimensionsBasedOnDataFields(modelStyle)) {
            return Object.keys(height.mappedValues);
        }

        if (this.isLayerDimensionsListItemPropertyBased(modelStyle)) {
            return height.mappedPropertyListItemIds;
        }

        throw new Error("Unhandled datafield based dimensional scaling");
    }

    public isLayerDimensionsBasedOnDataFields(modelStyle: ModelLayerStyle) {
        const { height, width, length } = modelStyle;

        if (height == null || width == null || length == null) {
            return false;
        }

        return (
            height.extractionMethod === StyleValueExtractionMethod.Mapped &&
            length.extractionMethod === StyleValueExtractionMethod.Mapped &&
            width.extractionMethod === StyleValueExtractionMethod.Mapped
        );
    }

    public isLayerDimensionsListItemPropertyBased(modelStyle: ModelLayerStyle) {
        const { height, width, length } = modelStyle;

        if (height == null || width == null || length == null) {
            return false;
        }

        return (
            height.extractionMethod === StyleValueExtractionMethod.MappedProperty &&
            length.extractionMethod === StyleValueExtractionMethod.MappedProperty &&
            width.extractionMethod === StyleValueExtractionMethod.MappedProperty
        );
    }
}
